// Sentry initialization for the React app.
// Imported once at the top of main.tsx (before <App /> renders) so it can
// catch errors during initial mount.

import * as Sentry from '@sentry/react';

const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;

if (dsn) {
  Sentry.init({
    dsn,
    environment: import.meta.env.MODE, // 'development' | 'production'
    release: import.meta.env.VITE_SENTRY_RELEASE as string | undefined,

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,       // mask user-typed text in replays
        blockAllMedia: true,     // skip images/videos
      }),
    ],

    // Performance — sample 10% of traffic in prod, 100% in dev
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,

    // Session Replay — only record when an error happens
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,

    // Trace requests to our own backend so backend Sentry can link to them
    tracePropagationTargets: [
      'localhost',
      /^https?:\/\/(?:.*\.)?ealoom\.com/,
      /\/api\//,
    ],

    beforeSend(event) {
      // Strip auth headers from breadcrumbs / requests
      if (event.request?.headers) {
        const h = event.request.headers as Record<string, string>;
        delete h.Authorization;
        delete h.authorization;
        delete h.Cookie;
        delete h.cookie;
      }
      return event;
    },
  });
} else {
  // eslint-disable-next-line no-console
  console.log('[Sentry] VITE_SENTRY_DSN not set — error reporting disabled');
}

// Restore user context on page load if user was already logged in
try {
  const stored = localStorage.getItem('user');
  if (stored) {
    const user = JSON.parse(stored);
    if (user?.id) {
      Sentry.setUser({
        id: String(user.id),
        email: user.email,
        username: user.username,
        role: user.role,
        company_id: user.company_id ? String(user.company_id) : undefined,
      });
    }
  }
} catch {
  // ignore — user context is best-effort
}

/** Tag the current Sentry scope with the logged-in user. Call after login. */
export function setSentryUser(user: {
  id: number | string;
  email?: string;
  username?: string;
  role?: string;
  company_id?: number | string;
}) {
  Sentry.setUser({
    id: String(user.id),
    email: user.email,
    username: user.username,
    role: user.role,
    company_id: user.company_id ? String(user.company_id) : undefined,
  });
}

/** Clear user context. Call on logout. */
export function clearSentryUser() {
  Sentry.setUser(null);
}

export { Sentry };
